import { useMemo } from "react";
import { useSelector } from "react-redux";

import {
  selectTotalCheckoutValue,
  selectTotalWeight,
} from "~/features/cart/cart/redux/selectors/checkoutPageSelectors";
import { selectTotalCartPriceWithoutDiscount } from "~/features/cart/cart/redux/selectors/cartSelectors";
import { KAMELEOON_FLAGS, useKameleoonFlag } from "~/common/config/kameleoonFlags";
import {
  selectCartContainsSurcharge,
  selectCartContainsTariffSurcharge,
  selectCartSurcharge,
} from "~/features/cart/cart/redux/selectors/cartSelectors";
import {
  selectCheckoutOrderablesViewModel,
  selectCheckoutBundleViewModels,
  selectQuotablesViewModel,
} from "~/features/cart/cartV2/redux/selectors/cartTableSelectors";
import { useCartPnaQueries } from "~/features/cart/cart/hooks/useCartPnaQueries";

export function useCheckoutCartViewModel() {
  const campaignDiscountEnabled = useKameleoonFlag(KAMELEOON_FLAGS.campaigndiscount);
  const totalDiscountSelector = useMemo(
    () => selectTotalCartPriceWithoutDiscount(campaignDiscountEnabled),
    [campaignDiscountEnabled],
  );
  const totalWeight = useSelector(selectTotalWeight);
  const totalSum = useSelector(selectTotalCheckoutValue);
  const totalDiscount = useSelector(totalDiscountSelector);
  const hasAnySurcharge = useSelector(selectCartContainsSurcharge);
  const hasAnyTariffSurcharge = useSelector(selectCartContainsTariffSurcharge);
  const surchargeValue = useSelector(selectCartSurcharge);

  // PnA subscriptions (A07): table VMs and place-order snapshots come from the query-backed
  // list hook instead of the legacy state.products dictionaries.
  const { descriptorFor, entrySnapshots } = useCartPnaQueries();

  const orderablesSelector = useMemo(() => selectCheckoutOrderablesViewModel(descriptorFor), [descriptorFor]);
  const bundlesSelector = useMemo(() => selectCheckoutBundleViewModels(descriptorFor), [descriptorFor]);
  const quotablesSelector = useMemo(() => selectQuotablesViewModel(descriptorFor), [descriptorFor]);
  const orderablesViewModel = useSelector(orderablesSelector);
  const bundleViewModels = useSelector(bundlesSelector);
  const quotablesViewModel = useSelector(quotablesSelector);

  return {
    totalWeight,
    totalSum,
    totalDiscount,
    hasAnySurcharge,
    hasAnyTariffSurcharge,
    surchargeValue,
    orderablesViewModel,
    bundleViewModels,
    quotablesViewModel,
    entrySnapshots,
  };
}
